import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Navigation from "../components/Navigation";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { FileText, TrendingUp, Target, MessageSquare, Users, AlertCircle, CheckCircle } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const menuItems = [
  { icon: FileText, label: "Mental Training Plan", path: "/plans" },
  { icon: TrendingUp, label: "Mental Performance", path: "/performance" },
  { icon: Target, label: "Goals", path: "/goals" },
  { icon: MessageSquare, label: "Messages", path: "/messages" },
  { icon: MessageSquare, label: "AI Coach", path: "/ai-coach" }
];

export default function SkillAssessment({ user, onLogout }) {
  const [assessments, setAssessments] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [selectedAssessment, setSelectedAssessment] = useState(null);
  const [formData, setFormData] = useState({
    skill_area: "",
    current_level: "",
    notes: ""
  });
  const navigate = useNavigate();

  const skillAreas = [
    "Focus & Concentration",
    "Confidence",
    "Emotional Control",
    "Communication",
    "Leadership",
    "Team Cohesion",
    "Performance Under Pressure",
    "Recovery from Mistakes"
  ];

  const levels = [
    { value: "beginner", label: "Beginner - Just starting to develop this skill" },
    { value: "developing", label: "Developing - Some experience, room for growth" },
    { value: "competent", label: "Competent - Solid foundation, consistent performance" },
    { value: "proficient", label: "Proficient - Strong skill, reliable under pressure" },
    { value: "expert", label: "Expert - Mastery level, can teach others" }
  ];

  useEffect(() => {
    fetchAssessments();
  }, []);

  const fetchAssessments = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/assessments`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAssessments(response.data);
    } catch (error) {
      toast.error("Failed to load assessments");
    }
  };

  const handleCreateAssessment = async () => {
    if (!formData.skill_area || !formData.current_level) {
      toast.error("Please select skill area and level");
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/assessments`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success("Assessment created successfully!");
      setShowCreate(false);
      setFormData({ skill_area: "", current_level: "", notes: "" });
      fetchAssessments();
    } catch (error) {
      toast.error("Failed to create assessment");
    }
  };

  const getLevelColor = (level) => {
    const colors = {
      beginner: "text-red-600 bg-red-50",
      developing: "text-orange-600 bg-orange-50",
      competent: "text-yellow-600 bg-yellow-50",
      proficient: "text-blue-600 bg-blue-50",
      expert: "text-green-600 bg-green-50"
    };
    return colors[level] || "text-gray-600 bg-gray-50";
  };

  const getLevelLabel = (level) => {
    const labels = {
      beginner: "Beginner",
      developing: "Developing",
      competent: "Competent",
      proficient: "Proficient",
      expert: "Expert"
    };
    return labels[level] || level;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-sky-50 via-blue-50 to-indigo-100">
      <Navigation user={user} onLogout={onLogout} />

      <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-4 sm:py-12">
        <div className="mb-12">
          <div className="flex justify-between items-start mb-8">
            <div>
              <h1 className="text-2xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
                Skill Assessment
              </h1>
              <p className="text-lg text-gray-600">
                Evaluate your mental skills and track your development
              </p>
            </div>
            <Button
              data-testid="new-assessment-btn"
              onClick={() => setShowCreate(true)}
              className="bg-sky-600 hover:bg-sky-700 flex items-center space-x-2"
            >
              <CheckCircle className="w-5 h-5" />
              <span>New Assessment</span>
            </Button>
          </div>
        </div>

        {/* Assessments Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
          {assessments.map((assessment) => (
            <Card
              key={assessment.id}
              data-testid={`assessment-card-${assessment.id}`}
              className="border-0 shadow-lg bg-white/90 backdrop-blur-sm hover:shadow-xl transition-all duration-300 cursor-pointer"
              onClick={() => setSelectedAssessment(assessment)}
            >
              <CardHeader>
                <div className="flex items-center justify-between mb-2">
                  <CardTitle className="text-lg">{assessment.skill_area}</CardTitle>
                  <div className={`inline-block px-3 py-1 rounded-full text-sm font-medium ${getLevelColor(assessment.current_level)}`}>
                    {getLevelLabel(assessment.current_level)}
                  </div>
                </div>
                <CardDescription>
                  Assessed: {new Date(assessment.created_at).toLocaleDateString()}
                </CardDescription>
              </CardHeader>
              {assessment.notes && (
                <CardContent>
                  <p className="text-sm text-gray-600 line-clamp-3">{assessment.notes}</p>
                </CardContent>
              )}
            </Card>
          ))}

          {assessments.length === 0 && (
            <div className="col-span-full text-center py-12">
              <AlertCircle className="w-16 h-16 mx-auto text-gray-400 mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">No assessments yet</h3>
              <p className="text-gray-600 mb-4">
                Create your first skill assessment to start tracking your mental game development
              </p>
              <Button data-testid="create-assessment-empty-btn" onClick={() => setShowCreate(true)} className="bg-sky-600 hover:bg-sky-700">
                Create Assessment
              </Button>
            </div>
          )}
        </div>

        {/* Create Assessment Modal */}
        {showCreate && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg p-6 max-w-md w-full max-h-[90vh] overflow-y-auto">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">New Skill Assessment</h2>
              
              <div className="space-y-4">
                <div>
                  <Label htmlFor="skill-area">Skill Area</Label>
                  <select
                    id="skill-area"
                    value={formData.skill_area}
                    onChange={(e) => setFormData({ ...formData, skill_area: e.target.value })}
                    className="w-full mt-1 px-3 py-2 border rounded-md bg-white"
                  >
                    <option value="">Select a skill area...</option>
                    {skillAreas.map((skill) => (
                      <option key={skill} value={skill}>{skill}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <Label htmlFor="current-level">Current Level</Label>
                  <select
                    id="current-level"
                    value={formData.current_level}
                    onChange={(e) => setFormData({ ...formData, current_level: e.target.value })}
                    className="w-full mt-1 px-3 py-2 border rounded-md bg-white"
                  >
                    <option value="">Select your current level...</option>
                    {levels.map((level) => (
                      <option key={level.value} value={level.value}>{level.label}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <Label htmlFor="notes">Notes (Optional)</Label>
                  <Textarea
                    id="notes"
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    placeholder="Any specific observations, goals, or areas you want to focus on..."
                    className="mt-1"
                    rows={3}
                  />
                </div>

                <div className="flex space-x-3 pt-4">
                  <Button
                    data-testid="submit-assessment-btn"
                    onClick={handleCreateAssessment}
                    className="flex-1 bg-sky-600 hover:bg-sky-700"
                  >
                    Create Assessment
                  </Button>
                  <Button
                    data-testid="cancel-assessment-btn"
                    onClick={() => {
                      setShowCreate(false);
                      setFormData({ skill_area: "", current_level: "", notes: "" });
                    }}
                    variant="outline"
                    className="flex-1"
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Assessment Detail Modal */}
        {selectedAssessment && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">{selectedAssessment.skill_area}</h2>
              
              <div className="space-y-4">
                <div className="flex items-center space-x-3">
                  <span className="text-gray-600">Current Level:</span>
                  <div className={`inline-block px-3 py-1 rounded-full text-sm font-medium ${getLevelColor(selectedAssessment.current_level)}`}>
                    {getLevelLabel(selectedAssessment.current_level)}
                  </div>
                </div>

                <div>
                  <span className="text-gray-600">Assessed on:</span>
                  <span className="ml-2 font-medium">
                    {new Date(selectedAssessment.created_at).toLocaleDateString()}
                  </span>
                </div>

                {selectedAssessment.notes && (
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-2">Notes:</h3>
                    <p className="text-gray-700 bg-gray-50 p-3 rounded-lg">
                      {selectedAssessment.notes}
                    </p>
                  </div>
                )}

                <Button
                  onClick={() => setSelectedAssessment(null)}
                  className="w-full bg-sky-600 hover:bg-sky-700"
                >
                  Close
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}